import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Activity,
  Battery,
  Car,
  ChevronLeft,
  ChevronRight,
  Clock,
  Lock,
  MapPin,
  Navigation,
  RefreshCw,
  Route,
  Unlock,
  Zap,
} from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import Sidebar from '../components/Sidebar';
import TopBar from '../components/TopBar';
import MapView from '../components/MapView';
import VehiclePanel from '../components/VehiclePanel';
import VehicleTypeIcon, { getVehicleTypeLabel } from '../components/VehicleTypeIcon';
import { positionService, vehicleService } from '../services/api';
import { useVehicleStore } from '../store';
import websocket from '../services/websocket';
import { toast } from 'react-toastify';

const getVehicleStatus = (vehicle) => {
  if (vehicle?.is_blocked) return { label: 'Bloqueado', className: 'badge-danger' };
  if (!vehicle?.is_active) return { label: 'Offline', className: 'badge-warning' };
  if (vehicle?.positions?.[0]?.motion) return { label: 'Em movimento', className: 'badge-success' };
  return { label: 'Parado', className: 'badge-info' };
};

const getLastUpdate = (vehicle) => {
  const deviceTime = vehicle?.positions?.[0]?.device_time;
  if (!deviceTime) return 'Sem posicao';

  return `ha ${formatDistanceToNow(new Date(deviceTime), { locale: ptBR })}`;
};

const formatCoordinate = (value) => {
  const number = Number(value);
  return Number.isFinite(number) ? number.toFixed(6) : '-';
};

const StatCard = ({ icon: Icon, label, value, tone = 'primary' }) => {
  const tones = {
    primary: 'bg-primary-500/15 text-primary-300',
    success: 'bg-success-500/15 text-success-300',
    warning: 'bg-warning-500/15 text-warning-300',
    danger: 'bg-danger-500/15 text-danger-300',
  };

  return (
    <div className="rounded-lg border border-dark-800 bg-dark-900 p-4">
      <div className="flex items-center justify-between gap-4">
        <div>
          <p className="text-xs font-medium uppercase tracking-wide text-dark-400">{label}</p>
          <p className="mt-1 text-2xl font-bold text-white">{value}</p>
        </div>
        <div className={`flex h-11 w-11 items-center justify-center rounded-lg ${tones[tone] || tones.primary}`}>
          <Icon className="h-5 w-5" />
        </div>
      </div>
    </div>
  );
};

const Tracking = () => {
  const {
    vehicles,
    setVehicles,
    selectedVehicle,
    selectVehicle,
    updateVehicle,
    setLoading,
    loading,
  } = useVehicleStore();

  const [rightPanelOpen, setRightPanelOpen] = useState(true);
  const [blockLoading, setBlockLoading] = useState(null);
  const [showTrail, setShowTrail] = useState(true);
  const [trailLimit, setTrailLimit] = useState(300);
  const [trailPositions, setTrailPositions] = useState([]);
  const [trailLoading, setTrailLoading] = useState(false);
  const selectedVehicleIdRef = useRef(null);
  const showTrailRef = useRef(showTrail);
  const trailLimitRef = useRef(trailLimit);

  useEffect(() => {
    loadVehicles();
    setupSocket();

    return () => {
      websocket.disconnect();
    };
  }, []);

  useEffect(() => {
    selectedVehicleIdRef.current = selectedVehicle?.id || null;
  }, [selectedVehicle?.id]);

  useEffect(() => {
    showTrailRef.current = showTrail;
  }, [showTrail]);

  useEffect(() => {
    trailLimitRef.current = trailLimit;
  }, [trailLimit]);

  useEffect(() => {
    if (!selectedVehicle && vehicles.length > 0) {
      selectVehicle(vehicles[0]);
    }
  }, [selectVehicle, selectedVehicle, vehicles]);

  useEffect(() => {
    if (!showTrail || !selectedVehicle?.id) {
      setTrailPositions([]);
      return;
    }

    loadVehicleTrail(selectedVehicle, trailLimit);
  }, [selectedVehicle?.id, showTrail, trailLimit]);

  const stats = useMemo(() => {
    const total = vehicles.length;
    const moving = vehicles.filter((vehicle) => vehicle.positions?.[0]?.motion).length;
    const online = vehicles.filter((vehicle) => vehicle.is_active && !vehicle.is_blocked).length;
    const blocked = vehicles.filter((vehicle) => vehicle.is_blocked).length;

    return { total, moving, online, blocked };
  }, [vehicles]);

  const selectedPosition = selectedVehicle?.positions?.[0];
  const selectedStatus = getVehicleStatus(selectedVehicle);

  const loadVehicles = async () => {
    setLoading(true);

    try {
      const response = await vehicleService.getAll();
      setVehicles(response.data.data);
    } catch (error) {
      toast.error(error.response?.data?.message || 'Erro ao carregar rastreamento');
    } finally {
      setLoading(false);
    }
  };

  const loadVehicleTrail = async (vehicle = selectedVehicle, limit = trailLimit) => {
    if (!vehicle?.id) {
      setTrailPositions([]);
      return;
    }

    setTrailLoading(true);

    try {
      const response = await positionService.getHistory(vehicle.id, { limit });
      const positions = (response.data.data || [])
        .slice()
        .reverse()
        .filter((position) => position.latitude && position.longitude);

      setTrailPositions(positions);
    } catch (error) {
      setTrailPositions([]);
      toast.error(error.response?.data?.message || 'Erro ao carregar rastro');
    } finally {
      setTrailLoading(false);
    }
  };

  const setupSocket = () => {
    const user = JSON.parse(localStorage.getItem('user'));
    if (!user) return;

    websocket.connect(user.id);
    websocket.on('position_update', (data) => {
      if (!data?.vehicleId || !data?.position) return;

      updateVehicle(data.vehicleId, {
        positions: [data.position],
        last_update: data.position.device_time || new Date().toISOString(),
      });

      setTrailPositions((current) => {
        if (!showTrailRef.current || data.vehicleId !== selectedVehicleIdRef.current) {
          return current;
        }

        const last = current[current.length - 1];
        const isSamePosition = last && (
          (last.id && data.position.id && last.id === data.position.id) ||
          (last.device_time && data.position.device_time && last.device_time === data.position.device_time)
        );

        if (isSamePosition) return current;

        return [...current, data.position].slice(-Number(trailLimitRef.current || 300));
      });
    });
  };

  const handleToggleBlock = async () => {
    if (!selectedVehicle) return;

    setBlockLoading(selectedVehicle.id);

    try {
      const response = await vehicleService.toggleBlock(selectedVehicle.id);
      updateVehicle(selectedVehicle.id, response.data.data);
      selectVehicle(response.data.data);
      toast.success(response.data.message);
    } catch (error) {
      toast.error(error.response?.data?.message || 'Erro ao alterar bloqueio');
    } finally {
      setBlockLoading(null);
    }
  };

  return (
    <div className="flex h-screen bg-dark-950">
      <Sidebar />

      <div className="flex flex-1 flex-col overflow-hidden">
        <TopBar />

        <main className="flex-1 overflow-y-auto p-6">
          <div className="mb-6 flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
            <div>
              <h1 className="text-2xl font-bold text-white">Rastreamento</h1>
              <p className="text-dark-400">Acompanhe a frota em tempo real pelo mapa.</p>
            </div>

            <div className="flex flex-col gap-3 sm:flex-row">
              <button
                type="button"
                onClick={() => setShowTrail((value) => !value)}
                className={`flex items-center justify-center gap-2 rounded-lg px-4 py-3 text-sm font-semibold uppercase transition ${
                  showTrail
                    ? 'bg-primary-500 text-white hover:bg-primary-600'
                    : 'border border-dark-700 bg-dark-900 text-dark-300 hover:border-primary-500 hover:text-white'
                }`}
              >
                <Route className="h-4 w-4" />
                Rastro
              </button>
              <select
                value={trailLimit}
                onChange={(event) => setTrailLimit(Number(event.target.value))}
                disabled={!showTrail}
                className="input-field h-12 w-full sm:w-36"
                title="Quantidade de pontos no rastro"
              >
                <option value={100}>100 pontos</option>
                <option value={300}>300 pontos</option>
                <option value={800}>800 pontos</option>
              </select>
              <button
                type="button"
                onClick={() => {
                  loadVehicles();
                  if (showTrail) loadVehicleTrail();
                }}
                disabled={loading || trailLoading}
                className="btn-secondary flex items-center justify-center gap-2"
              >
                <RefreshCw className={`h-4 w-4 ${loading || trailLoading ? 'animate-spin' : ''}`} />
                Atualizar
              </button>
            </div>
          </div>

          <div className="mb-6 grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
            <StatCard icon={Car} label="Veiculos" value={stats.total} />
            <StatCard icon={MapPin} label="Online" value={stats.online} tone="success" />
            <StatCard icon={Navigation} label="Em movimento" value={stats.moving} tone="warning" />
            <StatCard icon={Lock} label="Bloqueados" value={stats.blocked} tone="danger" />
          </div>

          <div className={`grid grid-cols-1 gap-6 transition-all duration-300 ${rightPanelOpen ? 'xl:grid-cols-[minmax(0,1fr)_370px]' : ''}`}>
            <div className="relative min-w-0">
              <MapView
                className="h-[calc(100vh-286px)] min-h-[520px]"
                layoutVersion={rightPanelOpen ? 1 : 0}
                trailPositions={showTrail ? trailPositions : []}
                showTrail={showTrail}
              />
              <button
                type="button"
                onClick={() => setRightPanelOpen((open) => !open)}
                className="absolute right-4 top-4 z-[500] flex h-11 w-11 items-center justify-center rounded-lg border border-dark-700 bg-dark-900/95 text-dark-200 shadow-xl shadow-black/30 transition-colors hover:border-primary-500 hover:bg-dark-800 hover:text-white"
                aria-label={rightPanelOpen ? 'Recolher painel lateral direito' : 'Mostrar painel lateral direito'}
                title={rightPanelOpen ? 'Recolher painel' : 'Mostrar painel'}
              >
                {rightPanelOpen ? <ChevronRight className="h-5 w-5" /> : <ChevronLeft className="h-5 w-5" />}
              </button>
            </div>

            {rightPanelOpen && (
              <aside className="space-y-6">
                <VehiclePanel vehicles={vehicles} />

                <section className="card">
                  <div className="mb-4 flex items-center justify-between gap-3">
                    <div>
                      <h2 className="text-xl font-bold text-white">Detalhes</h2>
                      <p className="text-sm text-dark-400">Veiculo selecionado no mapa.</p>
                    </div>
                    {selectedVehicle && (
                      <span className={`badge ${selectedStatus.className}`}>
                        {selectedStatus.label}
                      </span>
                    )}
                  </div>

                  {!selectedVehicle ? (
                    <div className="py-8 text-center text-dark-400">
                      <Car className="mx-auto mb-2 h-10 w-10 opacity-50" />
                      Selecione um veiculo.
                    </div>
                  ) : (
                    <div className="space-y-4">
                      <div className="flex items-start gap-3 rounded-lg border border-dark-700 bg-dark-800/60 p-4">
                        <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-lg border border-primary-500/30 bg-primary-500/10 text-primary-300">
                          <VehicleTypeIcon vehicle={selectedVehicle} className="h-6 w-6" />
                        </div>
                        <div className="min-w-0">
                          <h3 className="truncate font-semibold text-white">{selectedVehicle.name}</h3>
                          <p className="text-sm text-dark-400">{selectedVehicle.plate} - {getVehicleTypeLabel(selectedVehicle)}</p>
                          <p className="text-xs text-dark-500">IMEI: {selectedVehicle.imei || 'Nao informado'}</p>
                        </div>
                      </div>

                      <div className="grid grid-cols-2 gap-3 text-sm">
                        <div className="rounded-lg bg-dark-800 p-3">
                          <p className="mb-1 flex items-center gap-2 text-dark-400">
                            <Navigation className="h-4 w-4" />
                            Velocidade
                          </p>
                          <p className="font-semibold text-white">{Number(selectedPosition?.speed || 0).toFixed(0)} km/h</p>
                        </div>
                        <div className="rounded-lg bg-dark-800 p-3">
                          <p className="mb-1 flex items-center gap-2 text-dark-400">
                            <Battery className="h-4 w-4" />
                            Bateria
                          </p>
                          <p className="font-semibold text-white">{selectedPosition?.battery ?? '-'}%</p>
                        </div>
                        <div className="rounded-lg bg-dark-800 p-3">
                          <p className="mb-1 flex items-center gap-2 text-dark-400">
                            <Zap className="h-4 w-4" />
                            Ignicao
                          </p>
                          <p className="font-semibold text-white">{selectedPosition?.ignition ? 'Ligada' : 'Desligada'}</p>
                        </div>
                        <div className="rounded-lg bg-dark-800 p-3">
                          <p className="mb-1 flex items-center gap-2 text-dark-400">
                            <Clock className="h-4 w-4" />
                            Atualizacao
                          </p>
                          <p className="font-semibold text-white">{getLastUpdate(selectedVehicle)}</p>
                        </div>
                      </div>

                      <div className="rounded-lg bg-dark-800 p-3 text-sm">
                        <p className="mb-2 flex items-center gap-2 text-dark-400">
                          <MapPin className="h-4 w-4" />
                          Localizacao
                        </p>
                        <p className="text-white">{selectedPosition?.address || 'Endereco nao informado'}</p>
                        <p className="mt-2 text-xs text-dark-500">
                          Lat {formatCoordinate(selectedPosition?.latitude)} / Lon {formatCoordinate(selectedPosition?.longitude)}
                        </p>
                      </div>

                      <button
                        type="button"
                        onClick={handleToggleBlock}
                        disabled={blockLoading === selectedVehicle.id}
                        className={`flex w-full items-center justify-center gap-2 rounded-lg px-4 py-3 text-sm font-semibold uppercase transition ${
                          selectedVehicle.is_blocked
                            ? 'bg-success-500 text-white hover:bg-success-600'
                            : 'bg-danger-500 text-white hover:bg-danger-600'
                        } disabled:cursor-not-allowed disabled:opacity-70`}
                      >
                        {selectedVehicle.is_blocked ? <Unlock className="h-4 w-4" /> : <Lock className="h-4 w-4" />}
                        {blockLoading === selectedVehicle.id ? 'Enviando...' : selectedVehicle.is_blocked ? 'Desbloquear' : 'Bloquear'}
                      </button>
                    </div>
                  )}
                </section>
              </aside>
            )}
          </div>
        </main>
      </div>
    </div>
  );
};

export default Tracking;
